"use client";

import React from "react";
import { Phone, MapPin, Mail, LifeBuoy } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@west-tennessee-consulting/ui";
import { CompanyInfoCard, InfoRow } from "./CompanyInfoCard";
import type { CompanyEntity } from "@/lib/types/company";
import { ContactMethodActions } from "@/components/shared/ContactMethodActions/ContactMethodActions";

interface CompanyContactInfoProps {
  company: CompanyEntity;
}

interface SupportGroupResponse {
  emails: string[];
}

/**
 * Read-only enrolled Google support-group email(s) for the company. Backed by
 * the cheap DB-only `/support-group` endpoint (no Gmail read), so it's safe to
 * fetch eagerly on the overview. Enrollment rarely changes → generous staleTime.
 */
function useCompanySupportGroupEmails(companyId: string): string[] {
  const { getAuthToken } = useAuth();
  const { data } = useQuery<SupportGroupResponse>({
    queryKey: ["company-support-group", companyId],
    enabled: !!companyId,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const token = await getAuthToken();
      const res = await fetch(
        `/api/companies/${encodeURIComponent(companyId)}/support-group`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          cache: "no-store",
        },
      );
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      return (await res.json()) as SupportGroupResponse;
    },
  });
  return data?.emails ?? [];
}

export function CompanyContactInfo({ company }: CompanyContactInfoProps) {
  const fullAddress = [
    company.address,
    company.city,
    company.state,
    company.zip_code,
    company.country
  ].filter(Boolean).join(", ");

  const phone = company.organization_phone;

  const supportEmails = useCompanySupportGroupEmails(company.id);
  const supportInboxUrl = `/companies/${company.id}?tab=support-inbox`;

  const hasAnyData = fullAddress || phone || supportEmails.length > 0;

  if (!hasAnyData) return null;

  return (
    <CompanyInfoCard title="Contact Information" icon={Mail}>
      {phone && (
        <InfoRow
          label="Phone"
          value={
            <ContactMethodActions
              kind="phone"
              value={phone}
              entityContext={{
                item: company as unknown as Record<string, unknown>,
                entityType: "companies",
              }}
            />
          }
          icon={Phone}
        />
      )}

      {supportEmails.length > 0 && (
        <InfoRow
          label="Support email"
          value={
            <div className="text-sm text-gray-900 dark:text-gray-100">
              {supportEmails.map((email, index) => (
                <React.Fragment key={email}>
                  {index > 0 && ", "}
                  <a
                    href={supportInboxUrl}
                    className="text-primary hover:underline break-all"
                  >
                    {email}
                  </a>
                </React.Fragment>
              ))}
            </div>
          }
          icon={LifeBuoy}
        />
      )}

      {fullAddress && (
        <InfoRow
          label="Full Address"
          value={
            <div className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
              {fullAddress}
            </div>
          }
          icon={MapPin}
        />
      )}
    </CompanyInfoCard>
  );
}
